import { h } from 'preact';
import { m as motion, useTransform, useViewportScroll } from 'framer-motion';
import { useEffect, useState } from 'preact/hooks';
import colors from 'tailwindcss/colors';

import { useTyper, useBreakpoint } from '../../hooks';

import { DownloadMenu, Feature, Footer, Header, Logo } from '../../components';
import * as FeatureTheme from './components/FeatureTheme/FeatureTheme';
import * as FeaturePlugins from './components/FeaturePlugins/FeaturePlugins';
import * as FeatureSearch from './components/FeatureSearch/FeatureSearch';

import imgSearchSwitch from '../../assets/search-switch.svg';
import imgPlugins from '../../assets/plugins.svg';
import imgThemes from '../../assets/themes.svg';

function Home() {
  const { scrollY } = useViewportScroll();
  const searchBarY = useTransform(scrollY, [1700, 1850], [0, -30]);

  const [pluginText, setPluginText] = useState('');
  const [theme, setTheme] = useState('light');
  const bp = useBreakpoint();

  const text = useTyper([
    {
      text: 'Scroll to see what UnTab can do',
      enterPos: [-1, 0],
      exitPos: [0, 470],
    },
    {
      text: 'UI Design',
      enterPos: [470, 670],
      exitPos: [1380, 1450],
    },
    {
      text: `That's just the beginning! ✨`,
      enterPos: [1450, 1600],
      exitPos: [1700, 1850],
    },
    {
      text: '/',
      enterPos: [1850, 1900],
      exitPos: [2400, 2401],
    },
    {
      text: '',
      enterPos: [2401, 2402],
      exitPos: [3000, 3200],
    },
    {
      text: '/themes',
      enterPos: [3200, 3400],
      exitPos: [3599, 3600],
    },
    {
      text: '',
      enterPos: [3600, 3601],
      exitPos: [4648, 4649],
    },
    {
      text: 'Download UnTab today!',
      enterPos: [4650, 4850],
      exitPos: [9999, 9999],
    },
  ], { marginTop: 200 });

  useEffect(() => {
    return scrollY.onChange(v => {
      if (v >= 2600 && v <= 3200) {
        setPluginText('Tab actions');
      } else if (v >= 3800 && v <= 4650) {
        setPluginText('Themes');
      }
      else {
        setPluginText('');
      }

      if (bp.md && v >= 3800 && v < 4650) {
        setTheme('dark');
      } else {
        setTheme('light');
      }
    });
  }, [ scrollY, setPluginText, bp.md ]);

  const themeAnimation = {
    initial: {
      color: colors.coolGray[900],
      backgroundColor: colors.white,
    },
    animate: {
      color: colors.coolGray[900],
      backgroundColor: colors.white,
    },
    transition: {
      delay: 0,
      duration: 0.8,
    },
  };

  if (theme === 'dark') {
    themeAnimation.animate.backgroundColor = colors.coolGray[700];
    themeAnimation.animate.color = colors.coolGray[50];
  }

  return (
    <>
      <section class="bg-gray-50 relative" style={{ backgroundImage: `url(../../assets/dots.svg)`, backgroundSize: '4%' }}>
        <div class="absolute inset-0 bg-gradient-to-b from-gray-50 via-transparent to-gray-50" />
        <Header theme={theme} />
        <div class="relative z-10">
          <div class="container mx-auto px-8 md:flex lg:px-32 space-x-10 items-center pt-40 pb-28 md:pb-40">
            <div class="text-left md:text-center xl:text-left">
              <h1 class="text-5xl font-semibold max-w-4xl leading-tighter md:leading-tight mx-auto">
                Focus on <span class="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-purple-900">browsing the web</span> not the browser.
              </h1>
              <p class="font-medium mt-10 text-xl text-gray-500 max-w-3xl mx-auto leading-normal">
                UnTab is a browser extension that lets your search through tabs, history, actions and makes you look like a hero!
              </p>
            </div>
            <video class="mt-8 rounded w-1/2 hidden xl:block" loading="lazy" autoPlay loop muted playsInline>
              <source src="../../assets/videos/search.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </section>
      <main>
        <section class="hidden md:block" style={{ height: '340rem' }}>
          <div class="z-10 sticky top-32 -mt-68 container mx-auto px-8 lg:px-32">
            <div class="flex flex-col relative h-24">
              <FeatureSearch.FeatureSearchInfo />
              <FeaturePlugins.FeaturePluginsInfo />
              <FeatureTheme.FeatureThemeInfo />
            </div>
            <div class="mt-36">
              <div class="xl:w-1/2 w-full mx-auto relative">
                <FeatureSearch.FeatureSearchBrowserTabs />

                <motion.div
                  style={{ y: searchBarY }}
                  {...themeAnimation}
                  class="h-14 md:h-16 rounded-md shadow-xl font-medium flex items-center p-4 md:p-5 md:text-xl"
                >
                  {
                    !pluginText ? (
                      <div class="mr-1.5 text-gray-400">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <circle cx="11" cy="11" r="8" />
                          <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                      </div>
                    ) : (
                      <motion.div
                        class="rounded text-base font-medium flex items-center h-10 -ml-2 mr-1 px-3"
                        initial={{ backgroundColor: colors.coolGray[100] }}
                        animate={{ backgroundColor: theme === 'dark' ? colors.coolGray[600] : colors.coolGray[100] }}
                        transition={themeAnimation.transition}
                      >
                        {pluginText}
                      </motion.div>
                    )
                  }
                  <span class="p-1 pr-0">
                    {text}
                  </span>
                  <span class="w-px h-full bg-gray-500 animate-blink" />
                </motion.div>

                <FeatureSearch.FeatureSearchResult />
                <FeaturePlugins.FeaturePluginsResult />
                <FeatureTheme.FeatureThemeResult theme={theme} />
              </div>
            </div>
          </div>
        </section>
        <section class="md:hidden container mx-auto px-8 py-16 space-y-24">
          <section>
            <Feature.Name className="text-purple-500">
              <Feature.Icon src={imgSearchSwitch} alt="Search and Switch icon" />
              Search &amp; Switch
            </Feature.Name>
            <Feature.Headline className="from-purple-500 to-purple-900 bg-clip-text text-transparent">
              Tabs, History &amp; Bookmarks.
            </Feature.Headline>
            <Feature.Description className="text-gray-500">
              Searching through a huge list of tabs is a pain. With UnTab every tab, history and bookmark is just a few keystrokes away.
            </Feature.Description>

            {
              !bp.md && (
                <video class="mt-8 rounded" loading="lazy" autoPlay muted loop playsInline>
                  <source src="../../assets/videos/search.mp4" type="video/mp4" />
                </video>
              )
            }
          </section>

          <section>
            <Feature.Name className="text-green-500">
              <Feature.Icon src={imgPlugins} alt="Plugins icon" />
              Plugins
            </Feature.Name>
            <Feature.Headline className="from-green-500 to-green-800 bg-clip-text text-transparent">
              It's more than search.
            </Feature.Headline>
            <Feature.Description className="text-gray-500">
              Plugins extend the functionality of UnTab by making common actions accessible on every page.
              Want to do a quick search? math? close the tab? there's a plugin for it.
            </Feature.Description>

            {
              !bp.md && (
                <video class="mt-8 rounded" loading="lazy" autoPlay muted loop playsInline>
                  <source src="../../assets/videos/plugins.mp4" type="video/mp4" />
                </video>
              )
            }
          </section>

          <section>
            <Feature.Name className="text-blue-400">
              <Feature.Icon src={imgThemes} alt="Themes icon" />
              Themes
            </Feature.Name>
            <Feature.Headline className="from-blue-400 to-blue-900 bg-clip-text text-transparent">
              Beautiful themes.
            </Feature.Headline>
            <Feature.Description className="text-gray-500">
              UnTab comes with a bunch of themes to match your preferences and moods.
              Thanks to the open community of UnTab, themes can be contributed by anyone.
            </Feature.Description>

            {
              !bp.md && (
                <video class="mt-8 rounded" loading="lazy" autoPlay muted loop playsInline>
                  <source src="../../assets/videos/themes.mp4" type="video/mp4" />
                </video>
              )
            }
          </section>
        </section>
        
        <section class="md:-mt-16 py-24 md:py-36 relative bg-gray-50 border-t-4 border-purple-600 md:text-center" style={{ backgroundImage: 'url(../../assets/dots.svg)', backgroundSize: '4%' }}>
          <div class="absolute inset-0 bg-gradient-to-b from-gray-50 via-transparent to-gray-50" />
          <div class="relative z-10 container mx-auto px-8 lg:px-32">
            <h2 class="text-4xl md:text-5xl font-semibold">
              Get UnTab for your browser
            </h2>
            <p class="mt-6 text-lg font-medium text-gray-500">
              If you are using a Chromium based browser, you may try installing the Chrome version of UnTab.
            </p>
            <div class="mt-14 md:mt-20">
              <DownloadMenu />
            </div>
            <p class="text-sm mt-4 text-gray-600">Psst! It's totally Free!</p>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
}

export default Home;
